<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import PatientProfileFormFields from 'dashboard/components-next/Contacts/PatientProfileFormFields.vue';
import PatientProfilesAPI from 'dashboard/api/patientProfiles.js';
import { useAlert } from 'dashboard/composables';

const emit = defineEmits(['create']);

const { t } = useI18n();

const dialogRef = ref(null);
const contactsFormRef = ref(null);
const clinicalFieldsRef = ref(null);
const contact = ref(null);
const clinicalData = ref({});

const uiFlags = useMapGetter('contacts/getUIFlags');
const isCreatingContact = computed(() => uiFlags.value.isCreating);

const createNewContact = contactItem => {
  contact.value = contactItem;
};

const handleClinicalChange = data => {
  clinicalData.value = data;
};

// Verifica se algum campo clínico foi preenchido (precisa criar patient_profile?)
const hasClinicalData = () => {
  const d = clinicalData.value;
  return !!(
    d.procedure_id ||
    d.assigned_physician_id ||
    d.assigned_digital_human_id ||
    d.surgery_date ||
    d.birth_date ||
    d.gender ||
    d.medical_record_number ||
    d.primary_diagnosis ||
    d.exam_results ||
    d.medical_guidance ||
    d.notes
  );
};

const handleDialogConfirm = async () => {
  if (!contact.value) return;
  emit('create', contact.value);
};

// Chamado pelo componente pai quando o contato foi criado com sucesso.
// Aqui criamos o patient_profile vinculado se tiver dados clínicos preenchidos.
const onSuccess = async createdContact => {
  if (hasClinicalData() && createdContact?.id) {
    try {
      const payload = { contact_id: createdContact.id };
      Object.entries(clinicalData.value).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) payload[k] = v;
      });
      await PatientProfilesAPI.create(payload);
      useAlert('Dados clínicos salvos com sucesso');
    } catch (err) {
      useAlert(
        `Contato criado, mas falhou ao salvar dados clínicos: ${
          err?.response?.data?.error || err.message
        }`
      );
    }
  }
  contactsFormRef.value?.resetForm();
  clinicalFieldsRef.value?.reset();
  dialogRef.value.close();
};

const closeDialog = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef, contactsFormRef, onSuccess });
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    @confirm="handleDialogConfirm"
  >
    <div class="flex items-center justify-between gap-3">
      <h3 class="text-base font-medium leading-6 text-n-slate-12 mb-0">
        Novo paciente
      </h3>
      <Button
        icon="i-lucide-x"
        variant="ghost"
        color="slate"
        size="sm"
        class="flex-shrink-0 -mr-2"
        @click="closeDialog"
      />
    </div>
    <ContactsForm
      ref="contactsFormRef"
      is-new-contact
      @update="createNewContact"
    />
    <PatientProfileFormFields
      ref="clinicalFieldsRef"
      @change="handleClinicalChange"
    />
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          type="submit"
          :label="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVE_CONTACT')
          "
          color="blue"
          :disabled="contactsFormRef?.isFormInvalid"
          :is-loading="isCreatingContact"
        />
      </div>
    </template>
  </Dialog>
</template>
